import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { History, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { API } from '@/utils/api';

const hm = (iso) => (iso ? iso.slice(11, 16) : '');
const iso = (date, t) => (t ? `${date}T${t}:00` : null);

export const TimeEntryEditDialog = ({ emp, entry, date, onClose, onSaved }) => {
  const [ci, setCi] = useState(hm(entry?.clock_in));
  const [co, setCo] = useState(hm(entry?.clock_out));
  const [breaks, setBreaks] = useState((entry?.breaks || []).map(b => ({ s: hm(b.start_time), e: hm(b.end_time) })));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const setBreak = (i, k, v) => setBreaks(bs => bs.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const minutes = (b) => (b.s && b.e ? Math.round((new Date(iso(date, b.e)) - new Date(iso(date, b.s))) / 60000) : null);

  const save = async () => {
    if (reason.trim().length < 3) { toast.error('Indiquez le motif de la correction'); return; }
    if (!ci) { toast.error('Indiquez l\'heure d\'arrivée'); return; }
    setBusy(true);
    try {
      await axios.put(`${API}/timeclock/entries/${emp.id}/${date}`, {
        clock_in: iso(date, ci), clock_out: iso(date, co), reason,
        breaks: breaks.filter(b => b.s).map(b => ({ start_time: iso(date, b.s), end_time: iso(date, b.e), duration_minutes: minutes(b) })),
      });
      toast.success(entry ? 'Timbrage corrigé' : 'Timbrage ajouté'); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (reason.trim().length < 3) { toast.error('Indiquez le motif de la suppression'); return; }
    if (!window.confirm('Supprimer le timbrage de ce jour ?')) return;
    try { await axios.delete(`${API}/timeclock/entries/${emp.id}/${date}`, { params: { reason } }); toast.success('Timbrage supprimé'); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="time-edit-dialog">
        <DialogHeader><DialogTitle>{entry ? 'Corriger' : 'Ajouter'} le timbrage — {emp.first_name} {emp.last_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">{new Date(`${date}T12:00:00`).toLocaleDateString('fr-CH', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Arrivée</Label><Input type="time" value={ci} onChange={e => setCi(e.target.value)} data-testid="time-edit-in" /></div>
          <div><Label>Départ</Label><Input type="time" value={co} onChange={e => setCo(e.target.value)} data-testid="time-edit-out" /></div>
        </div>
        <div>
          <div className="flex items-center justify-between"><Label>Pauses</Label>
            <Button size="sm" variant="outline" onClick={() => setBreaks(b => [...b, { s: '', e: '' }])} data-testid="time-edit-add-break"><Plus className="w-3 h-3 mr-1" />Pause</Button></div>
          <div className="space-y-2 mt-2">{breaks.map((b, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`time-edit-break-${i}`}>
              <Input type="time" value={b.s} onChange={e => setBreak(i, 's', e.target.value)} className="h-9" data-testid={`time-edit-break-${i}-start`} />
              <span>→</span>
              <Input type="time" value={b.e} onChange={e => setBreak(i, 'e', e.target.value)} className="h-9" data-testid={`time-edit-break-${i}-end`} />
              <span className="text-xs text-slate-500 w-14">{minutes(b) != null ? `${minutes(b)} min` : ''}</span>
              <button type="button" onClick={() => setBreaks(bs => bs.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" data-testid={`time-edit-break-${i}-remove`}><Trash2 className="w-4 h-4" /></button>
            </div>))}
            {breaks.length === 0 && <p className="text-xs text-slate-400">Aucune pause saisie (les pauses automatiques s'appliquent).</p>}
          </div>
        </div>
        <div><Label>Motif (obligatoire)</Label>
          <Textarea rows={2} value={reason} onChange={e => setReason(e.target.value)} placeholder="Ex : oubli de timbrer le matin" data-testid="time-edit-reason" /></div>
        {(entry?.correction_log || []).length > 0 && (
          <div className="rounded-lg bg-slate-50 border p-2 text-xs space-y-1" data-testid="time-edit-history">
            <p className="font-semibold flex items-center gap-1"><History className="w-3 h-3" />Historique</p>
            {entry.correction_log.slice().reverse().map((l, i) => <p key={i}>{new Date(l.timestamp).toLocaleString('fr-CH')} · {l.by || 'admin'} · {l.action || 'correction'}{l.reason ? ` — ${l.reason}` : ''}</p>)}
          </div>
        )}
        <div className="flex justify-between gap-2">
          {entry ? <Button variant="outline" className="text-red-600" onClick={remove} data-testid="time-edit-delete">Supprimer</Button> : <span />}
          <div className="flex gap-2"><Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={save} disabled={busy} data-testid="time-edit-save">Enregistrer</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
